import React, { FC, useState } from "react";
import { ApiKey } from "../../../../../types";
import { Box, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DeleteApiKeyModal } from "../../modals/DeleteApiKeyModal";
import { getTime } from "../../../../../utils/getTime";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AvTimerIcon from "@mui/icons-material/AvTimer";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface PlanItemProps {
  apiKey: ApiKey;
  gridTemplateColumns?: string;
}

export const ApiKeyItem: FC<PlanItemProps> = ({
  apiKey,
  gridTemplateColumns = "1fr",
}) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState(false);

  function copyTextToClipboard(text: string): void {
    if (!navigator.clipboard) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    } else {
      navigator.clipboard
        .writeText(text)
        .then(() => {})
        .catch((err) => {});
    }
  }

  return (
    <>
      <Box
        sx={{
          height: "40px",
          borderBottom: "1px solid #e0e0e0",
          display: { xs: "none", sm: "none", md: "grid" },
          gridTemplateColumns: gridTemplateColumns,
          px: "24px",
          paddingBottom: "8px",
          paddingTop: "8px",
          background: "#fff",
          "&:hover": {
            background: "#f5f5f5",
          },
          "&:active": {
            background: "#f5f5f5",
          },
          "&:last-child": {
            borderBottom: "none",
            borderRadius: "0 0 16px 16px",
            mb: "24px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
          }}
        >
          <Typography
            sx={{
              width: "80%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#666666",
            }}
          >
            {apiKey.key || "-"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
          }}
        >
          <Typography
            sx={{
              width: "80%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#666666",
            }}
          >
            {apiKey.expiration_at ? (
              <Typography
                sx={{ fontSize: "0.875rem", color: "rgb(108, 115, 127)" }}
              >
                {new Date(apiKey.expiration_at).toDateString()}{" "}
                {getTime(new Date(apiKey.expiration_at))}
              </Typography>
            ) : (
              "-"
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
          }}
        >
          <Typography
            sx={{
              width: "80%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "#666666",
            }}
          >
            {apiKey.read_only ? t("Yes") : t("No")}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mr: "8px",
          }}
        >
          <DeleteOutlineIcon
            sx={{ color: "#666666", cursor: "pointer" }}
            onClick={() => {
              setModal(true);
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          height: "80px",
          borderBottom: "1px solid #e0e0e0",
          display: { xs: "flex", sm: "flex", md: "none" },
          flexDirection: "column",
          px: "12px",
          paddingBottom: "8px",
          paddingTop: "8px",
          background: "#fff",
          "&:hover": {
            background: "#f5f5f5",
          },
          "&:active": {
            background: "#f5f5f5",
          },
          "&:last-child": {
            borderBottom: "none",
            borderRadius: "0 0 16px 16px",
            mb: "24px",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex" }}>
            <Typography
              sx={{
                maxWidth: "200px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#666666",
              }}
            >
              {apiKey.key || "-"}
            </Typography>
            <ContentCopyIcon
              sx={{ color: "#666666", mr: "4px", cursor: "pointer" }}
              fontSize="small"
              onClick={() => {
                copyTextToClipboard(apiKey.key);
                toast.info(t("apiKeys.key_copied"));
              }}
            />
          </Box>
          <DeleteOutlineIcon
            sx={{ color: "#666666", cursor: "pointer", mr: "12px" }}
            onClick={() => {
              setModal(true);
            }}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "0.8fr 1fr",
            justifyContent: "space-between",
            mt: "24px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "8px",
            }}
          >
            <AvTimerIcon sx={{ color: "#666666", mr: "8px" }} />
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#666666",
                ml: "4px",
              }}
            >
              {apiKey.expiration_at ? (
                <Typography
                  sx={{ fontSize: "0.875rem", color: "rgb(108, 115, 127)" }}
                >
                  {new Date(apiKey.expiration_at).toDateString()}{" "}
                  {/* {getTime(new Date(apiKey.expiration_at))} */}
                </Typography>
              ) : (
                "-"
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "nowrap",
                color: "#666666",
              }}
            >
              {t("apiKeys.read_only")}:
            </Typography>
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: "#666666",
                ml: "4px",
              }}
            >
              {apiKey.read_only ? t("yes") : t("no")}
            </Typography>
          </Box>
        </Box>
      </Box>
      <DeleteApiKeyModal
        id={apiKey.id}
        open={modal}
        handleClose={() => setModal(false)}
      />
    </>
  );
};
